// Export event schemas and types (excluding auth types to avoid conflicts)
export {
  // Base schemas
  coordinatesSchema,
  dateSchema,
  urlSchema,
  priceSchema,
  eventCategorySchema,
  // Event schemas
  eventSchema,
  eventUpdateSchema,
  eventSearchSchema,
  createEventSchema,
  nearbyEventsQuerySchema,
  eventSearchQuerySchema,
  unifiedSearchQuerySchema,
  unifiedSearchSortBySchema,
  bboxEventsQuerySchema,
  popularAreasQuerySchema,
  landmarksQuerySchema,
  reverseGeocodeQuerySchema,
  eventCategoriesResponseSchema,
  eventChangeTypeSchema,
  eventSubscriptionSchema,
  eventChangeSchema,
  areaSubscriptionArgsSchema,
  venueSchema,
  artistSchema,
  eventbriteEventSchema,
  // Date filtering schema
  dateFilterSchema,
  // Geo and venue schemas
  venueTypeSchema,
  geoPolygonSchema,
  transitStopSchema,
  venueMetadataSchema,
  locationFiltersSchema,
  advancedSearchRequestSchema,
  // Unified search schemas
  searchSortSchema,
  // Data quality schemas
  dataQualityMetricsSchema,
  duplicateDetectionResultSchema,
  manualReviewItemSchema,
  // Notification preferences (API-1489)
  notificationPreferencesSchema,
  NOTIFICATION_PREFERENCE_DEFAULTS,
  // Types
  type Event,
  type EventUpdate,
  type CreateEvent,
  type EventSearch,
  type NearbyEventsQuery,
  type BboxEventsQuery,
  type EventSearchQuery,
  type Venue,
  type Artist,
  type EventCategory,
  type DataQualityMetrics,
  type DuplicateDetectionResult,
  type ManualReviewItem,
  type PopularAreasQuery,
  type LandmarksQuery,
  type ReverseGeocodeQuery,
  type VenueType,
  type GeoPolygon,
  type TransitStop,
  type VenueMetadata,
  type LocationFilters,
  type AdvancedSearchRequest,
  type EventCategoriesResponse,
  type EventChangeType,
  type EventSubscription,
  type EventChange,
  type AreaSubscriptionArgs,
  type EventbriteEvent,
  type SearchSort,
  type UnifiedSearchQuery,
  type UnifiedSearchSortBy,
  type NotificationPreferences,
  type ResolvedNotificationPreferences,
} from './schemas';

// Export authentication schemas and types
export * from './auth';

// Export error handling utilities
export * from './errors';

// Export search params functionality
export * from './search-params';

// Export saved-search schemas (FRE-926)
export * from './saved-search';

// Export haversine distance utilities (canonical meters + km/miles wrappers)
export * from './haversine';

// Export Ticketmaster route request schemas
export * from './ticketmaster';
export * from './ticketResale';
export * from './deadOutboundHosts';
export * from './venueBoxOffice';

// Export admin management schemas (API-1267)
export * from './admin';

// Export Refine admin Venues CRUD schemas (API-1472..1475 / M3)
export * from './admin/venue';

// PIP-408 — admin venue-candidate list / promote / attach schemas
export * from './admin/venueCandidate';

// API-1810 — admin-issued account-recovery magic link
export * from './admin/accountRecovery';

// Export Refine admin sponsored-listing schemas (API-1362 / M1 — Revenue v0)
export * from './admin/sponsored';

// Self-serve advertiser create + onboarding draft (WEB-40) — reuses admin advertiser input
export * from './advertisers';

// Apex contact inquiry (WEB-569)
export * from './contact';

// Venue-claim APIs (API-1626): self-serve advertiser claims + admin queue/actions
export * from './venueClaims';

// Advertiser portal reads (WEB-587): claimable venues + sponsorable events
export * from './advertiserPortal';

// Export Refine admin events CRUD schemas (API-1468..71)
export {
  adminCreateEventSchema,
  adminUpdateEventSchema,
  adminDeleteEventSchema,
  adminEventsListQuerySchema,
  adminEventsListSortFieldSchema,
  adminEventsListSortDirectionSchema,
  adminEventsFlagFilterSchema,
  adminEventStatusSchema,
  adminQualityStatusSchema,
  adminQualityReasonCodeSchema,
  adminEventIdParamSchema,
  adminDecideEventIdParamSchema,
  adminEventDecisionSchema,
  adminDecideEventSchema,
  parseAdminEventsListSort,
  ADMIN_EVENTS_LIST_DEFAULT_PAGE_SIZE,
  ADMIN_EVENTS_LIST_MAX_PAGE_SIZE,
  ADMIN_EVENTS_FLAG_FILTERS,
  type AdminCreateEventBody,
  type AdminUpdateEventBody,
  type AdminDeleteEventBody,
  type AdminEventsListQuery,
  type AdminEventsListSort,
  type AdminEventsFlagFilter,
  type AdminEventStatus,
  type AdminQualityStatus,
  type AdminQualityReasonCode,
  type AdminEventDecision,
  type AdminDecideEventBody,
} from './admin/event';

// Export Refine admin Users list + update schemas (API-1567, API-1571)
export {
  adminUsersListQuerySchema,
  adminUsersListSortFieldSchema,
  adminUsersListSortDirectionSchema,
  adminUserRoleSchema,
  adminUserStatusFilterSchema,
  adminUserDerivedStatusSchema,
  parseAdminUsersListSort,
  ADMIN_USERS_LIST_DEFAULT_PAGE_SIZE,
  ADMIN_USERS_LIST_MAX_PAGE_SIZE,
  type AdminUsersListQuery,
  type AdminUsersListSort,
  type AdminUserRole,
  type AdminUserStatusFilter,
  type AdminUserDerivedStatus,
  // Refine admin Users summary shapes (API-1568)
  type AdminUsersSummary,
  type AdminUsersSummarySignupBucket,
  type AdminUsersSummaryRecentlyJoined,
  // Refine admin Users update schema (API-1571)
  adminUpdateUserSchema,
  type AdminUpdateUserBody,
} from './admin/user';

// Export Refine admin flagged-events review-queue schemas (WEB-452)
export {
  adminFlaggedEventsListQuerySchema,
  adminFlaggedEventReviewStatusSchema,
  adminFlaggedEventQualityStatusSchema,
  adminFlaggedEventFilterSchema,
  adminFlaggedEventSortSchema,
  ADMIN_FLAGGED_EVENTS_LIST_DEFAULT_PAGE_SIZE,
  ADMIN_FLAGGED_EVENTS_LIST_MAX_PAGE_SIZE,
  type AdminFlaggedEventsListQuery,
  type AdminFlaggedEventReviewStatus,
  type AdminFlaggedEventQualityStatus,
  type AdminFlaggedEventFilter,
  type AdminFlaggedEventSort,
  type AdminFlaggedEventCounts,
} from './admin/flaggedEvent';

// Recommendation response fragments (mirrors OpenAPI RecommendationEvent extensions)
export * from './recommendations';

// Export saved-events response schemas
export {
  savedEventPayloadSchema,
  savedEventApiItemSchema,
  savedEventsPaginationSchema,
  savedEventsResponseSchema,
  type SavedEventPayload,
  type SavedEventApiItem,
  type SavedEventsPagination,
  type SavedEventsApiResponse,
} from './savedEvents';

// Export Place Details schemas (FRE-660)
export {
  PLACE_DETAILS_CARD_V1_FIELD_MASK,
  PLACE_DETAILS_CARD_V1_FIELD_MASK_HEADER,
  PLACE_DETAILS_LEGACY_FIELD_MASK,
  PLACE_DETAILS_LEGACY_FIELD_MASK_HEADER,
  PLACE_DETAILS_BASIC_FIELDS,
  PLACE_DETAILS_POPULARITY_FIELDS,
  PLACE_DETAILS_CONTACT_FIELDS,
  PLACE_DETAILS_ATMOSPHERE_FIELDS,
  PLACE_DETAILS_VERSION,
  PLACE_ID_PATTERN,
  isValidPlaceId,
  placeIdSchema,
  placeCardDetailsV1Schema,
  rawPlaceDetailsV1Schema,
  type PlaceCardDetailsV1,
  type PlaceDetailsVersion,
  type RawPlaceDetailsV1,
} from './placeDetails';

export {
  FORBIDDEN_PLACES_ATMOSPHERE_AMENITY_FIELDS,
  type ForbiddenPlacesAtmosphereAmenityField,
} from './placesAtmosphereAmenityFields';

// COR-61 — shared Nearby Search request shape (web GettingThere + data-pipeline
// transit derivation). FieldMask is Essentials-only; do not add Enterprise or
// Atmosphere amenity SKUs.
export {
  NEARBY_SEARCH_URL,
  NEARBY_SEARCH_FIELD_MASK,
  NEARBY_SEARCH_FIELD_MASK_FIELDS,
  NEARBY_SEARCH_TRANSIT_TYPES,
  NEARBY_SEARCH_PARKING_TYPES,
  NEARBY_SEARCH_DEFAULT_RADIUS_METERS,
  NEARBY_SEARCH_MAX_RADIUS_METERS,
  NEARBY_SEARCH_MIN_MAX_RESULT_COUNT,
  NEARBY_SEARCH_MAX_MAX_RESULT_COUNT,
  NEARBY_SEARCH_DEFAULT_MAX_RESULT_COUNT,
  nearbySearchResponseSchema,
  buildNearbySearchRequestBody,
  mapNearbySearchPlaces,
} from './placesNearbySearchContract';
export type {
  NearbyPlaceType,
  NearbySearchRankPreference,
  NearbySearchRequestInput,
  NearbySearchPlace,
  NearbySearchResponse,
} from './placesNearbySearchContract';

// Export service-to-service auth validation
export {
  validateServiceAuth,
  type ServiceName,
  type ServiceKeyEntry,
  type ServiceKeyRegistry,
  type ServiceAuthConfig,
  type ServiceAuthRequest,
  type ServiceAuthResult,
  type ServiceAuthSuccess,
  type ServiceAuthFailure,
} from './serviceAuth';

// Export trusted user-context header propagation utilities
export {
  USER_CONTEXT_HEADERS,
  buildUserContextHeaders,
  extractUserContextFromHeaders,
  type UserContextHeaderName,
  type PropagatedUserContext,
  type BuildableAuthContext,
  type HeaderBag,
} from './service-context';

// Ingestion pipeline normalized event DTO (PIP-8)
export {
  normalizedEventSchema,
  normalizedEventCategorySchema,
  platformEventCategorySchema,
  normalizedEventStatusSchema,
  type NormalizedEvent,
} from './normalizedEvent';

export { computeDedupKey } from './dedupKey';

// Discover surface query schemas (WEB-52 · M7.2)
export {
  discoverQuerySchema,
  DEFAULT_DISCOVER_RADIUS_KM,
  discoverViewSchema,
  discoverSortSchema,
  discoverTimeOfDaySchema,
  discoverDateKeywordSchema,
  offeredDiscoverDateKeywordSchema,
  type DiscoverQuery,
  type DiscoverView,
  type DiscoverSort,
  type DiscoverTimeOfDay,
  type DiscoverDateKeyword,
  type DiscoverDateFilter,
} from './events';

// API-1820 — the one definition of the Discover date windows (R4/R5).
export {
  DATE_WINDOW_KEYWORDS,
  DATE_WINDOW_PRESETS,
  NEXT30_LOCAL_DAYS,
  NEXT7_DURATION_MS,
  RETIRED_DATE_WINDOW_KEYWORDS,
  STARTED_WITHOUT_END_DEFAULT_MS,
  TONIGHT_ENDS_LOCAL_HOUR,
  WEEKEND_STARTS_LOCAL_HOUR,
  addLocalDays,
  canonicalDateWindowKeyword,
  dateWindowPreset,
  isDateWindowPreset,
  isLocalDateKey,
  localDateKey,
  localHour,
  localTimeToInstant,
  localWeekday,
  minutesSinceStartWithoutEnd,
  resolveCityDateWindowKeyword,
  resolveDateWindow,
  resolveDateWindowKeyword,
  resolveLocalDateRangeWindow,
  type DateWindow,
  type DateWindowFilter,
  type DateWindowKeyword,
  type DateWindowPreset,
  type DateWindowPresetDefinition,
  type RetiredDateWindowKeyword,
} from './dateWindows';

export {
  NEIGHBORHOODS_MAX_LIMIT,
  neighborhoodsCityQuerySchema,
  neighborhoodsListQuerySchema,
  neighborhoodListItemSchema,
  neighborhoodsListResponseSchema,
  neighborhoodCategoryKeySchema,
  neighborhoodCategoryAccentSchema,
  neighborhoodCategoryEntrySchema,
  neighborhoodCategoriesResponseSchema,
  neighborhoodDensityDayParamSchema,
  neighborhoodDensityQuerySchema,
  neighborhoodDensityFeelSchema,
  neighborhoodDensityResponseSchema,
  neighborhoodBearingSchema,
  neighborhoodAdjacentSchema,
  neighborhoodTransitSchema,
  geoJsonPolygonSchema,
  geoJsonPointSchema,
  geoJsonBboxSchema,
  neighborhoodDetailSchema,
  neighborhoodDetailResponseSchema,
  topVenuesQuerySchema,
  topVenueEntrySchema,
  topVenuesResponseSchema,
  TOP_VENUES_DEFAULT_LIMIT,
  TOP_VENUES_MAX_LIMIT,
} from './neighborhoods';
export type {
  NeighborhoodsCityQuery,
  NeighborhoodsListQuery,
  NeighborhoodListItem,
  NeighborhoodsListResponse,
  NeighborhoodCategoryKey,
  NeighborhoodCategoryAccent,
  NeighborhoodCategoryEntry,
  NeighborhoodCategoriesResponse,
  NeighborhoodDensityDayParam,
  NeighborhoodDensityQuery,
  NeighborhoodDensityFeel,
  NeighborhoodDensityResponse,
  NeighborhoodBearing,
  NeighborhoodAdjacent,
  NeighborhoodTransit,
  GeoJsonPolygon,
  GeoJsonPoint,
  GeoJsonBbox,
  NeighborhoodDetail,
  NeighborhoodDetailResponse,
  TopVenuesQuery,
  TopVenueEntry,
  TopVenuesResponse,
} from './neighborhoods';

// API-1701 — city code → IANA timezone. The single source every surface that
// reads a LOCAL wall clock out of a stored UTC instant must resolve through,
// instead of inlining 'America/New_York'.
export {
  CITY_TIME_ZONE_MAP,
  DEFAULT_CITY_TIME_ZONE,
  cityTimeZone,
  isKnownCityTimeZone,
} from './cityTimeZone';

// Venue marketing-city surface (API-1397+)
export {
  VENUES_DEFAULT_LIMIT,
  VENUES_UPCOMING_WINDOW_DAYS,
  VENUES_EVENT_YIELD_WINDOW_DAYS,
  VENUES_MAX_LIMIT,
  // API-1705 — `?sort=` vocabulary for `GET /api/v1/venues`.
  VENUES_LIST_SORTS,
  VENUES_LIST_DEFAULT_SORT,
  venueListCursorSort,
  isVenueListNameCursor,
  VENUE_CITY_CODE_MAP,
  VENUE_PROGRAMMING_FLAG_KEYS,
  VENUE_PROGRAMMING_FLAG_LABELS,
  isKnownVenueCityCode,
  venueCityCodeSchema,
  venueSlugSchema,
  venueLookupParamSchema,
  venuePaginationMetaSchema,
  venueListPaginationMetaSchema,
  venueListPageRange,
  venuesListQuerySchema,
  venueListItemSchema,
  venueListResponseSchema,
  VENUE_AMENITY_SOURCE_PROVIDERS,
  venueAmenitySourceProviderSchema,
  venueAmenitySourceSchema,
  venueAmenitySchema,
  venueAccessibilitySchema,
  venueParkingSchema,
  venueTransitSchema,
  venueHouseRuleSchema,
  venueProgrammingFlagSchema,
  venueProgrammingMetaSchema,
  venueAddressSchema,
  venueCoordinatesSchema,
  VENUE_TYPE_KIND_LABELS,
  toVenueKindDisplayLabel,
  venueDetailResponseSchema,
  venueShowBadgeSchema,
  venuesShowsQuerySchema,
  venueShowSchema,
  venueShowsResponseSchema,
  venueStatsPriceRangeSchema,
  venueStatsResponseSchema,
  // API-1695 — `/venues/facets`: per-neighborhood and per-chip counts plus the
  // collection-wide aggregates WEB-864 needs.
  venueFacetsQuerySchema,
  venueNeighborhoodFacetSchema,
  venueCategoryFacetSchema,
  venueTierFacetSchema,
  venueCapacityFacetSchema,
  venueFacetsResponseSchema,
  encodeVenueListCursor,
  decodeVenueListCursor,
  encodeVenueShowsCursor,
  decodeVenueShowsCursor,
  // WEB-876 — the `sort=name` ordering key is length-bounded, and the cursor
  // cap is derived from that bound, so no venue name can mint a cursor the API
  // rejects. `apps/web` and `@loci/db-core` both read these rather than
  // restating the numbers.
  VENUE_CURSOR_MAX_LENGTH,
  VENUE_NAME_SORT_KEY_MAX_CHARS,
  venueNameSortKey,
} from './venues';
export {
  VENUE_LATE_NIGHT_START_HOUR_LOCAL,
  VENUE_LIST_CATEGORY_SLUGS,
  VENUE_LIST_MAJOR_CAPACITY_MIN,
  VENUE_LIST_MID_CAPACITY_MIN,
  VENUE_PAGE_CATEGORY_FACETS,
  VENUE_PAGE_CATEGORY_SLUGS,
  // WEB-958 — the type → shelf table both halves of the COR-66 rule read.
  VENUE_TYPE_SHELF,
  deriveVenueListCategory,
  isBreweryOutrankingShelf,
  isKnownVenueListCategory,
  isKnownVenuePageCategory,
  isProgrammingFacet,
} from './venueListCategory';
export type {
  VenueListCategorySlug,
  VenueListCategoryInput,
  VenuePageCategoryFacet,
  VenuePageCategorySlug,
} from './venueListCategory';
export type {
  VenuePaginationMeta,
  VenueListPaginationMeta,
  VenuesListQuery,
  VenueListItem,
  VenueListResponse,
  VenueAmenity,
  VenueAmenitySource,
  VenueAmenitySourceProvider,
  VenueAccessibility,
  VenueParking,
  VenueTransit,
  VenueHouseRule,
  VenueProgrammingFlag,
  VenueProgrammingMeta,
  VenueAddress,
  VenueCoordinates,
  VenueTypeKind,
  VenueDetailResponse,
  VenueShowBadge,
  VenuesShowsQuery,
  VenueShow,
  VenueShowsResponse,
  VenueStatsPriceRange,
  VenueStatsResponse,
  VenueFacetsQuery,
  VenueNeighborhoodFacet,
  VenueCategoryFacet,
  VenueTierFacet,
  VenueCapacityFacet,
  VenueFacetsResponse,
  VenuesListSort,
  VenueListCursorPayload,
  VenueListNameCursorPayload,
  VenueListUpcomingCursorPayload,
  VenueShowsCursorPayload,
} from './venues';

// Data-quality scorer input mapping (COR-17). `toScorableEvent` maps a
// persisted Prisma Event row (with location/categories/eventSource relations)
// to the flat ScorableEvent view from @loci/types.
export { toScorableEvent } from './scorableEvent';
export type { ScorableEventInput } from './scorableEvent';

// Data-quality authoritative scorer (COR-18). `scoreEventForPlacement` is the
// ONE pure/deterministic scorer that maps a ScorableEvent to a 0–100 score,
// QualityStatus band, machine-readable reasons, and per-dimension subscores.
// `isAddressLikeVenueName` (COR-25) is the address-shaped-venue-name detector
// behind the `LOCATION_NAME_IS_ADDRESS` hard fail — exported so the PIP-145
// targeted re-score can apply the SAME authoritative verdict to its candidate
// set instead of forking the heuristic.
// API-1700 additionally exports `STREET_TYPE_TOKENS` — the street-type half of
// that same signature — so db-core can build its raw-SQL counterpart from the
// one list instead of forking it into a Postgres regex. API-1708 exports
// `LEADING_HOUSE_NUMBER` (the other half) so db-core's SQL spelling of it is
// pinned to this regex by a test rather than drifting silently.
// PIP-407 exports `standingProductTitleNoun` — the title rule behind the
// `IDENTITY_TITLE_NON_EVENT` cap — so tooling can explain the verdict.
export {
  isAddressLikeVenueName,
  LEADING_HOUSE_NUMBER,
  scoreEventForPlacement,
  standingProductTitleNoun,
  STREET_TYPE_TOKENS,
} from './scoreEventForPlacement';
export type { PlacementScore, ScorePlacementContext } from './scoreEventForPlacement';

// `Location.kind` derivation (PIP-87 / PIP-289 / API-1717). Lives beside
// COR-25 because it delegates to it, and here rather than in data-pipeline so
// the admin venue PATCH (backend-api) re-derives `kind` with the SAME rule the
// ingest writers use — `classifyVenueKind` for a row being created,
// `rederiveVenueKind` for an existing row, from its stored columns.
export {
  classifyVenueKind,
  isPlaceholderVenueName,
  normalizeVenueLabel,
  PLACEHOLDER_VENUE_NAMES,
  rederiveVenueKind,
  VENUE_LABEL_SEPARATOR_PATTERN,
  venueKindToPrisma,
} from './venueKind';
export type { StoredVenueKindInputs, VenueKindLabel, VenueKindPrismaValue } from './venueKind';

// FRE-925: explicit follow-graph payload schemas. Mirror the @loci/types
// follow shapes; `followedListQuerySchema` coerces + bounds the list
// pagination query params.
export {
  artistFollowStateSchema,
  venueFollowStateSchema,
  followStateSchema,
  followedArtistNextShowSchema,
  followedArtistSchema,
  followedVenueSchema,
  followedListPaginationSchema,
  followedArtistsResponseSchema,
  followedVenuesResponseSchema,
  markSeenResponseSchema,
  followedListQuerySchema,
} from './follows';
export type {
  ArtistFollowStateValidated,
  VenueFollowStateValidated,
  FollowStateValidated,
  FollowedArtistValidated,
  FollowedVenueValidated,
  FollowedArtistsResponseValidated,
  FollowedVenuesResponseValidated,
  FollowedListQueryValidated,
} from './follows';
